<template>
  <vl-grid v-if="!isAdmin" mod-stacked mod-v-bottom mod-space-between>
    <vl-column width="3" width-m="12">
      <vl-checkbox v-model="filter.onlyActive" mod-switch>{{ $t('publications.list.filter.onlyActive') }}</vl-checkbox>
    </vl-column>
    <vl-column v-if="isDigitaalVlaanderen" width="4" width-m="12">
      <vl-form-message-label for="productGroup">{{
        $t('publications.list.filter.productGroup')
      }}</vl-form-message-label>
      <PbsSelect
        id="productGroup"
        v-model="filter.productGroup"
        :placeholder="$t('vl-select.placeholder')"
        :options="productGroupOptions"
        :custom-label="(e) => e.label"
      />
    </vl-column>
  </vl-grid>
  <vl-grid v-if="isAdmin">
    <vl-column>
      <div class="pbs-form-group">
        <vl-grid mod-stacked>
          <vl-column width="8" width-s="8" width-xs="12">
            <vl-form-message-label for="name">{{ $t('publications.list.filter.name') }}</vl-form-message-label>
            <vl-input-field
              id="name"
              v-model="filter.name"
              name="name"
              type="search"
              :placeholder="$t('publications.list.filter.searchName')"
              mod-block
              @keyup.enter="doSearch"
            />
          </vl-column>
          <vl-column width="4" width-s="4" width-xs="12">
            <vl-form-message-label for="publicationType">{{
              $t('publications.list.filter.publicationType')
            }}</vl-form-message-label>
            <PbsSelect
              id="publicationType"
              v-model="filter.type"
              value-property="name"
              :searchable="false"
              :placeholder="$t('vl-select.placeholder')"
              :options="publicationTypes"
              :custom-label="(e) => e.name"
            />
          </vl-column>
          <vl-column width="4" width-s="4" width-xs="12">
            <vl-form-message-label for="organisation">{{
              $t('deliveryprocessdefinition.list.filter.organisation.label')
            }}</vl-form-message-label>
            <PbsSelect
              id="organisation"
              v-model="filter.organisation"
              :placeholder="$t('vl-select.placeholder')"
              :options="organisationOptions"
              :custom-label="getOrganisationLabel"
            />
          </vl-column>
          <vl-column width="4" width-s="4" width-xs="12">
            <vl-form-message-label for="statusId">{{ $t('publications.list.filter.status') }}</vl-form-message-label>
            <PbsSelect
              id="statusId"
              v-model="filter.status"
              value-property="name"
              :searchable="false"
              :placeholder="$t('vl-select.placeholder')"
              :options="statusTypes"
              :custom-label="(e) => $t(`publications.states.publication.${e.name?.toLowerCase()}`)"
            />
          </vl-column>
          <vl-column v-if="isDigitaalVlaanderen" width="4" width-s="4" width-xs="12">
            <vl-form-message-label for="productGroup">{{
              $t('publications.list.filter.productGroup')
            }}</vl-form-message-label>
            <PbsSelect
              id="productGroup"
              v-model="filter.productGroup"
              :placeholder="$t('vl-select.placeholder')"
              :options="productGroupOptions"
              :custom-label="(e) => e.label"
            />
          </vl-column>
          <vl-column v-vl-spacer:top.small width="12" width-s="12" width-xs="12">
            <vl-action-group>
              <vl-button @click="doSearch">{{ $t('publications.list.filter.search') }}</vl-button>
              <vl-button mod-secondary @click="clear">{{ $t('publications.list.filter.clear') }}</vl-button>
            </vl-action-group>
          </vl-column>
          <vl-column width="3" width-s="3" width-xs="12"> </vl-column>
        </vl-grid>
      </div>
    </vl-column>
  </vl-grid>
</template>
<script lang="ts" setup>
import { EnumerationDto, OrganisationSearchResultDto } from '@/api/backoffice/clients';
import { ProductGroup } from '@/api/frontoffice/clients';
import { ENUM_PublicationTypes, ENUM_StatusTypes } from '@/modules/core/common';
import { Ref, computed, nextTick, reactive, ref, onMounted, watch } from 'vue';
import PbsSelect from '@/components/forms/PbsSelect.vue';
import { useOrganisationStore } from '@/modules/configuration/store/organisation/organisation.store';
import { useProductGroupOptionsStore } from '@/modules/publications/store/product-group-options.store';
import { useUserStore } from '@/store/user.store';
import { PublicationListOptions } from '@/modules/publications/store/publication-list.store';

const organisationStore = useOrganisationStore();
const productGroupOptionsStore = useProductGroupOptionsStore();
const userStore = useUserStore();

const organisationOptions: Ref<OrganisationSearchResultDto[]> = ref([]);
const productGroupOptions: Ref<ProductGroup[]> = ref([]);

const emit = defineEmits(['search', 'clear']);

const props = defineProps<{
  loading?: boolean;
  options?: PublicationListOptions;
}>();

const isAdmin = computed(() => {
  return userStore.isSysteembeheerder;
});

// The product groups are configured by, and only meaningful to, Digitaal Vlaanderen. The filter is
// offered to all of their users, admin or not, so it sits in both the admin and the non-admin block.
const isDigitaalVlaanderen = computed(() => {
  return userStore.isDigitaalVlaanderen;
});

const findOrganisation = (id: string | number) => {
  if (!id) {
    return null;
  }
  const item = organisationOptions.value.find((o) => o.id === parseInt(id?.toString()));
  return item;
};

const findProductGroup = (label: string) => {
  if (!label) {
    return null;
  }
  return productGroupOptions.value.find((g) => g.label === label);
};

const findPublicationType = (type: string | number): string => {
  const item = publicationTypes.value.find((t) => t.id === type);
  return item ? item.name : null;
};

const filter = reactive({
  name: null,
  type: null,
  status: null,
  organisation: null,
  productGroup: null,
  onlyActive: null
});

// True until the component has finished mounting. While it is, the filters are still being synced
// from the store, and reacting to that would search on half-applied values.
let initialising = true;

// The non-admin block has no search button, so its filters search on change. Watching them together
// keeps a change that touches both to a single search.
watch(
  () => [filter.onlyActive, filter.productGroup],
  () => {
    if (!isAdmin.value && !initialising) {
      doSearch();
    }
  }
);

watch(
  () => props.options,
  (newValue) => {
    updateFilter(newValue);
  },
  { deep: true }
);

const updateFilter = (newFilter: PublicationListOptions) => {
  filter.name = newFilter.filter?.name || null;
  filter.status = newFilter.filter?.status || null;
  filter.type = findPublicationType(newFilter.filter?.type) || null;
  filter.organisation = findOrganisation(newFilter.filter?.organisationId) || null;
  filter.productGroup = findProductGroup(newFilter.filter?.productGroupLabel) || null;

  const onlyActive = newFilter.filter?.onlyActive;
  if (onlyActive !== undefined && onlyActive !== null) {
    filter.onlyActive = onlyActive;
  } else if (!isAdmin.value) {
    filter.onlyActive = true;
  } else {
    filter.onlyActive = null;
  }
};

const statusTypes = computed((): EnumerationDto[] => {
  return [ENUM_StatusTypes.Actief, ENUM_StatusTypes.NietActief];
});

const publicationTypes = computed((): EnumerationDto[] => {
  return Object.values(ENUM_PublicationTypes);
});

const getOrganisationLabel = (organisation: OrganisationSearchResultDto) => {
  return `${organisation?.name} (${organisation?.ovoNumber})`;
};

onMounted(async () => {
  // The organisation list is a configuration endpoint and only the admin block offers it, so asking
  // for it as a dataleverancier would only earn a 403.
  if (isAdmin.value) {
    organisationOptions.value = await organisationStore.getOrganisations();
  }
  if (isDigitaalVlaanderen.value) {
    productGroupOptions.value = await productGroupOptionsStore.getProductGroups();
  }
  if (props.options) {
    updateFilter(props.options);
  }

  // Let the watchers that updateFilter just queued run while they are still ignored, then hand the
  // filter over with one search. That search must not reset the pager: the page comes from the query
  // string and this is not the reader changing a filter.
  await nextTick();
  initialising = false;
  emitSearch({ resetPager: false });
});

const emitSearch = (options: { resetPager: boolean }) => {
  emit(
    'search',
    {
      name: filter.name,
      status: filter.status,
      type: filter.type,
      organisation: filter.organisation,
      productGroup: filter.productGroup,
      onlyActive: filter.onlyActive
    },
    options
  );
};

// Bound to the search button, so it takes no arguments: a click event would land in them.
const doSearch = () => {
  emitSearch({ resetPager: true });
};

const clear = () => {
  filter.name = null;
  filter.type = null;
  filter.status = null;
  filter.organisation = null;
  filter.productGroup = null;
  emit('clear');
};
</script>
<style lang="scss" scoped>
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colorsRaw.scss';
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colors.scss';

.pbs-form-group {
  background: $vl-alt-bg;
  padding: 3rem;
}
.vl-grid {
  flex-grow: 1;
}
</style>
